/**
 * Vehicle browser — miller columns, one per level of whatever tree the source
 * declares, each with its own raw-payload toggle.
 *
 * Nothing here is hard-coded to "manufacturer → model → type": the levels come
 * from `GET /v1/vehicles/:source`, so a source with four levels (or a source
 * added next year) renders without a change.
 */
import { useEffect, useState } from 'react';
import { useApi } from '../lib/api';
import { useResource } from '../lib/hooks';
import { Banner, Empty, ErrorBox, Loading, cx } from '../ui';

interface Level {
  key: string;
  label: string;
  op: string;
  args: string[];
  idField: string;
  labelField: string;
  extraFields?: string[];
}

interface Node {
  id: string;
  label: string;
  extra?: Record<string, unknown>;
}

interface LevelResp {
  source: string;
  level: Level;
  nodes: Node[];
  raw: unknown;
  meta: { total: number; cache: string };
}

/** One column: the level's nodes, or its raw payload — the toggle is per level. */
function Column({
  source,
  level,
  args,
  selected,
  onSelect,
}: {
  source: string;
  level: Level;
  args: Record<string, string>;
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const { request } = useApi();
  const [data, setData] = useState<LevelResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [raw, setRaw] = useState(false);

  const key = level.args.map((a) => args[a]).join('/');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setData(null);

    const q = new URLSearchParams();
    for (const a of level.args) if (args[a]) q.set(a, args[a]!);

    request<LevelResp>('GET', `/v1/vehicles/${source}/${level.key}?${q.toString()}`)
      .then((r) => {
        if (cancelled) return;
        if (r.ok) setData(r.data);
        else {
          const d = r.data as { error?: string; op?: string };
          setError(d?.error ? `${d.error}${d.op ? ` (op: ${d.op})` : ''}` : `HTTP ${r.status}`);
        }
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, level.key, key]);

  return (
    <div className="mcol rawbox">
      <h4>
        {level.label}
        {data && <span className="ml-1.5 text-faint">{data.nodes.length}</span>}
        <button
          className={cx('colraw', raw && 'on')}
          title="Raw payload for this level"
          onClick={() => setRaw((v) => !v)}
        >
          {'{ }'}
        </button>
      </h4>

      {loading ? (
        <Loading />
      ) : error ? (
        <div className="p-2">
          <ErrorBox>{error}</ErrorBox>
        </div>
      ) : raw ? (
        <pre className="raw m-raw">{JSON.stringify(data?.raw, null, 2)}</pre>
      ) : !data || data.nodes.length === 0 ? (
        <Empty>Nothing here.</Empty>
      ) : (
        <div className="m-table">
          {data.nodes.map((n) => (
            <button
              key={n.id}
              className={cx('mitem', selected === n.id && 'on')}
              onClick={() => onSelect(n.id)}
              title={n.id}
            >
              <span>{n.label}</span>
              {n.extra && (
                <span className="ml-2 text-faint">
                  {Object.values(n.extra)
                    .filter((v) => v !== null && v !== '')
                    .join(' · ')}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Vehicles({ source }: { source: string }) {
  const { data: tree, loading, error } = useResource<{ source: string; levels: Level[] }>(`/v1/vehicles/${source}`);
  // Selected id per level key.
  const [picked, setPicked] = useState<Record<string, string>>({});

  function select(level: Level, id: string, levels: Level[]) {
    // Choosing at one level invalidates everything below it.
    const idx = levels.findIndex((l) => l.key === level.key);
    const next: Record<string, string> = {};
    for (let i = 0; i < idx; i++) {
      const k = levels[i]!.key;
      if (picked[k]) next[k] = picked[k]!;
    }
    next[level.key] = id;
    setPicked(next);
  }

  if (loading) return <Loading />;
  if (error || !tree) return <ErrorBox>{error ?? 'No vehicle tree for this source.'}</ErrorBox>;

  // A level is shown once every ancestor it needs has been chosen.
  const visible = tree.levels.filter((l) => l.args.every((a) => picked[a]));

  return (
    <>
      <Banner icon="⚙">
        Brand → model → version, as this catalog defines it. Every column has its own{' '}
        <span className="mono">{'{ }'}</span> toggle showing the raw payload for that level.
      </Banner>

      <div className="miller">
        {visible.map((level) => (
          <Column
            key={level.key}
            source={source}
            level={level}
            args={picked}
            selected={picked[level.key] ?? null}
            onSelect={(id) => select(level, id, tree.levels)}
          />
        ))}
      </div>

      <div className="mt-2 text-[11.5px] text-faint">
        Levels come from the server's declared tree for <b>{source}</b> —{' '}
        {tree.levels.map((l) => `${l.label} (${l.op})`).join(' → ')}. Every call is cached and appears in the request
        log.
      </div>
    </>
  );
}
